import { supabase } from '@/lib/supabase';

export interface BatchGrowthData {
  shadowBot?: any;
  mergeAnalysis?: any;
  stats?: {
    winRate: number;
    maxDrawdownDollar: number;
    finalProfit: number;
    totalTrades: number;
  };
}

/**
 * Batch Growth Data Service - Fetches ALL growth data in 2-3 queries instead of N queries
 */
export class PropBotBatchGrowthService {
  /**
   * Fetch ALL shadow bots and merge analyses for multiple bots in PARALLEL
   */
  static async getBatchGrowthData(botIds: string[]): Promise<Map<string, BatchGrowthData>> {
    if (!botIds || botIds.length === 0) {
      return new Map();
    }

    console.log(`🚀 [BatchGrowth] Fetching growth data for ${botIds.length} bots...`);

    try {
      // Step 1: Get all signal tokens for these bots
      const { data: bots } = await supabase
        .from('bots')
        .select('id, signal_token')
        .in('id', botIds);

      if (!bots || bots.length === 0) {
        console.warn('⚠️ No bots found');
        return new Map();
      }

      // Create reverse token mapping
      const reversedTokens = bots
        .filter(b => b.signal_token)
        .map(b => b.signal_token.split('').reverse().join(''));

      const tokenToBotIdMap = new Map(
        bots.map(b => [b.signal_token?.split('').reverse().join(''), b.id])
      );

      // Step 2: Fetch ALL shadow bots in SINGLE query
      const { data: shadowBots } = await supabase
        .from('bots')
        .select('*')
        .in('signal_token', reversedTokens)
        .eq('type', 'shadow_bot');

      console.log(`✅ [BatchGrowth] Fetched ${shadowBots?.length || 0} shadow bots`);

      // Step 3: Fetch ALL merge analyses in SINGLE query
      const shadowBotIds = shadowBots?.map(sb => sb.id) || [];
      const { data: mergeAnalyses } = shadowBotIds.length > 0
        ? await supabase
            .from('bot_merge_analysis_runs')
            .select('*')
            .in('parent_bot_id', shadowBotIds)
            .eq('published', true)
            .order('created_at', { ascending: false })
        : { data: [] };

      console.log(`✅ [BatchGrowth] Fetched ${mergeAnalyses?.length || 0} merge analyses`);

      // Step 4: Map shadow bots to original bot IDs
      const shadowBotMap = new Map(
        shadowBots?.map((sb: any) => {
          const originalToken = sb.signal_token?.split('').reverse().join('');
          const originalBotId = tokenToBotIdMap.get(originalToken);
          return [originalBotId, sb] as [string, any];
        }).filter(([id]) => id) as Array<[string, any]> || []
      );

      // Step 5: Map merge analyses to shadow bots
      const mergeAnalysisMap = new Map(
        mergeAnalyses?.map(ma => [ma.parent_bot_id, ma]) || []
      );

      // Step 6: Build result map with calculated stats
      const resultMap = new Map<string, BatchGrowthData>();

      for (const botId of botIds) {
        const shadowBot = shadowBotMap.get(botId);
        const mergeAnalysis = shadowBot ? mergeAnalysisMap.get((shadowBot as any).id) : undefined;

        // Calculate stats from merge analysis or shadow bot
        let stats = undefined;
        if (mergeAnalysis?.merged_stats) {
          stats = {
            winRate: mergeAnalysis.merged_stats.winRate || 0,
            maxDrawdownDollar: mergeAnalysis.merged_stats.maxDrawdownDollar || 0,
            finalProfit: mergeAnalysis.merged_stats.finalProfit || 0,
            totalTrades: mergeAnalysis.merged_stats.totalTrades || 0,
          };
        } else if (shadowBot) {
          // Fallback to shadow bot stats if available
          stats = {
            winRate: 0,
            maxDrawdownDollar: 0,
            finalProfit: 0,
            totalTrades: 0,
          };
        }

        resultMap.set(botId, {
          shadowBot,
          mergeAnalysis,
          stats,
        });
      }

      console.log(`✅ [BatchGrowth] Built growth data map for ${resultMap.size} bots`);
      return resultMap;
    } catch (error) {
      console.error('❌ [BatchGrowth] Error:', error);
      return new Map();
    }
  }
}
